import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Select } from "@/components/kit";

const RELATIONS = ["Spouse / Partner", "Child", "Parent", "Sibling", "Grandparent", "Grandchild", "Other relative", "Roommate", "Other"];
const box = "bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]";

// Family/household members editor. `base` is the API prefix:
//   "/membership/me"  or  "/membership/members/{member_id}"
export default function FamilyMembers({ base, family = [], onChanged, canPromote = false, onPromote, testid = "family-members" }) {
  const [f, setF] = useState({ name: "", relation: "", year_of_birth: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!f.name.trim()) return toast.error("Enter a name.");
    setBusy(true);
    try {
      await api.post(`${base}/family`, { name: f.name.trim(), relation: f.relation, year_of_birth: f.year_of_birth });
      toast.success("Family member added");
      setF({ name: "", relation: "", year_of_birth: "" });
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`${base}/family/${id}`); toast.success("Removed"); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div data-testid={testid}>
      {family.length === 0 ? (
        <p className="text-[13px] text-ink/50 mb-2" data-testid="family-empty">No family or household members added yet.</p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {family.map((fm) => (
            <div key={fm.id} data-testid={`family-row-${fm.id}`} className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2">
              <div className="min-w-0">
                <div className="text-[15px] font-semibold truncate">{fm.name}</div>
                <div className="text-[12px] text-ink/55">{[fm.relation, fm.year_of_birth ? `b. ${fm.year_of_birth}` : ""].filter(Boolean).join(" · ") || "Household member"}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canPromote && !fm.promoted && (
                  <button data-testid={`family-promote-${fm.id}`} onClick={() => onPromote && onPromote(fm)} className="text-[12px] font-semibold text-dark-olive px-2 py-1 rounded hover:bg-olive/15">Promote to member</button>
                )}
                <button data-testid={`family-remove-${fm.id}`} onClick={() => remove(fm.id)} className="text-maroon/70 hover:text-maroon p-1" aria-label="Remove"><Icon name="X" className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <input data-testid="family-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={box} />
        <Select data-testid="family-relation" value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} className={box}>
          <option value="">Relation…</option>
          {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
        <input data-testid="family-yob" value={f.year_of_birth} onChange={(e) => setF({ ...f, year_of_birth: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="Year of birth" inputMode="numeric" maxLength={4} className={box + " sm:w-32"} />
      </div>
      <Button variant="olive" data-testid="family-add" onClick={add} disabled={busy} className="mt-2 px-3 py-2 min-h-[40px] text-[14px]"><Icon name="Plus" className="w-4 h-4" /> Add family member</Button>
    </div>
  );
}
